import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import backendClient from '../../services/backendClient';
import {
  trackPromoLandingViewed,
  trackPromoCTAClicked,
} from '../../analytics/events';
import RecipeContent from '../Recipes/RecipeContent';
import { matchIngredientsForBatch } from '../../utils/groceryProcessor';
import { getUserCountrySync } from '../../services/apiService';
import countryFoodPreferences from '../../utils/country_food_preferences.json';

const PROMO_STICKY_ANIMATIONS_ID = 'promo-sticky-cta-animations';
const PROMO_STICKY_ANIMATIONS_CSS = `
@keyframes promoSavePulseGreen {
  0%, 100% {
    box-shadow:
      0 2px 16px 0 rgba(0, 0, 0, 0.12) inset,
      0 0 32px 0 rgba(255, 255, 255, 0.4) inset,
      0 0 0 2px rgba(0, 153, 0, 0.4),
      0 0 0 0 rgba(0, 200, 83, 0);
  }
  50% {
    box-shadow:
      0 2px 16px 0 rgba(0, 0, 0, 0.12) inset,
      0 0 32px 0 rgba(255, 255, 255, 0.4) inset,
      0 0 0 2px rgba(0, 153, 0, 0.5),
      0 0 16px 4px rgba(0, 200, 83, 0.45);
  }
}
.promo-save-pulse {
  animation: promoSavePulseGreen 2.4s ease-in-out infinite;
}
`;

function ensurePromoStickyAnimationsInjected() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(PROMO_STICKY_ANIMATIONS_ID)) return;
  const style = document.createElement('style');
  style.id = PROMO_STICKY_ANIMATIONS_ID;
  style.textContent = PROMO_STICKY_ANIMATIONS_CSS;
  document.head.appendChild(style);
}

function PromoStickyCTA({ onClick, label, hidden = false }) {
  const [readyToShow, setReadyToShow] = useState(false);

  useEffect(() => {
    ensurePromoStickyAnimationsInjected();
    const raf = requestAnimationFrame(() => setReadyToShow(true));
    return () => cancelAnimationFrame(raf);
  }, []);

  if (typeof document === 'undefined') return null;

  const offScreen = hidden || !readyToShow;

  return createPortal(
    <div
      className="
        fixed left-[20px] right-[20px] bottom-[16px]
        py-2 px-2
        flex justify-center
        bg-[linear-gradient(135deg,_rgba(243,243,242,0.8)_0%,_rgba(243,243,242,0.9)_100%)]
        backdrop-blur-[64px]
        z-50
        mx-auto
        max-w-2xl
        rounded-[96px]
        border-[1px] border-[#fff]
        shadow-[0px_0px_4px_0px_rgba(13,12,7,0.1),_1px_-2px_16px_0px_rgba(13,12,7,0.1)]
      "
      style={{
        transform: offScreen ? 'translate3d(0, calc(100% + 150px), 0)' : 'translate3d(0, 0, 0)',
        transition: 'transform 600ms cubic-bezier(0.22, 1, 0.36, 1)',
        willChange: 'transform',
        pointerEvents: offScreen ? 'none' : 'auto',
      }}
    >
      <button
        onClick={onClick}
        className="
          inline-flex justify-center items-center gap-1
          h-[48px] px-[24px] py-[14px]
          rounded-[32px]
          border-[1px] border-[linear-gradient(200deg,_#090_0%,_#060_100%)]
          text-[#FFF] text-center
          text-[14px] font-medium leading-[28px]
          transition-colors
          w-full
          cursor-pointer
          bg-[linear-gradient(200deg,_#090_0%,_#060_100%)]
          md:hover:border-[0.5px] md:hover:border-[#060] md:hover:bg-[#060]
          shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(0,102,0,0.4)]
          promo-save-pulse
        "
      >
        <span>{label}</span>
      </button>
    </div>,
    document.body
  );
}

function PromoBottomHero({ onClick }) {
  const { t } = useTranslation('pages');
  const [currentDescriptionIndex, setCurrentDescriptionIndex] = useState(0);

  const rotatingDescriptionsValue = t('landing.rotatingDescriptions', {
    returnObjects: true,
  });
  const rotatingDescriptions = Array.isArray(rotatingDescriptionsValue)
    ? rotatingDescriptionsValue
    : [];

  useEffect(() => {
    if (rotatingDescriptions.length <= 1) return undefined;
    const interval = setInterval(() => {
      setCurrentDescriptionIndex((prev) => (prev + 1) % rotatingDescriptions.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [rotatingDescriptions.length]);

  const userCountry = getUserCountrySync();
  const currencySymbol =
    countryFoodPreferences?.[userCountry]?.currencySymbol ||
    countryFoodPreferences?.Spain?.currencySymbol ||
    '$';

  return (
    <section
      className="hero-section mt-12 flex overflow-hidden justify-center items-center py-12 md:py-16"
      style={{
        backgroundColor: '#FFCB00',
        backgroundImage: "url('/assets/landing/hero-bg.jpg'), linear-gradient(rgb(255, 203, 0) 20%, rgba(255, 234, 154, 0.2) 60%, rgba(255, 203, 0, 0.8) 100%)",
        backgroundSize: 'cover',
        backgroundPosition: 'top center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="relative z-10 container mx-auto pt-8 pb-8 md:py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-center md:gap-10 max-w-5xl mx-auto">
          <div className="md:flex-1 md:max-w-xl flex flex-col">
            <div className="max-w-5xl mx-auto md:mx-0 text-center px-6 order-1">
              <div className="content-stretch flex flex-col items-center relative shrink-0" data-name="Title">
                <div className="content-stretch flex items-center justify-center relative shrink-0 w-full" data-name="H1.Editorial">
                  <p className="leading-[52px] md:leading-[60px] font-extrabold relative shrink-0 text-[#0d0c07] md:text-[48px] text-[40px] tracking-[-0.3328px]">
                    {t('landing.hero.title')}
                  </p>
                </div>
                <div className="content-stretch flex items-center justify-center relative shrink-0 w-full" data-name="H3">
                  <p className="leading-[44px] md:leading-[52px] italic font-bold relative shrink-0 text-[#0d0c07] md:text-[36px] text-[32px] tracking-[-0.3328px]">
                    {t('landing.hero.tagline')}
                  </p>
                </div>
              </div>

              <div className="content-stretch flex items-start justify-start relative shrink-0 w-full md:mt-6 mt-4" data-name="p.Editorial.Regular">
                <p className="leading-[32px] relative text-center text-[#0d0c07] text-[17.2px] w-full">
                  <Trans
                    i18nKey="landing.hero.description"
                    t={t}
                    components={{ bold: <span className="font-bold" /> }}
                  />
                </p>
              </div>
            </div>

            <div className="relative md:mt-2 mt-0 flex justify-center md:hidden order-2">
              <div className="relative">
                <div className="flex items-center justify-center relative">
                  <div className="text-8xl px-8">
                    <img
                      src="/assets/hero-banner-image.png"
                      alt={t('landing.hero.imageAlt')}
                      className="mx-auto mb-2 max-w-full max-h-[272px] md:max-h-[400px]"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="max-w-5xl mx-auto md:mx-0 text-center md:text-center px-6 md:mt-8 mt-3 order-3">
              <button
                className="
                  inline-flex justify-center items-center gap-1
                  h-[56px] px-[40px] py-[14px] mb-6
                  rounded-[32px]
                  border-[1px] border-[linear-gradient(200deg,_#090_0%,_#060_100%)]
                  bg-[#090]
                  text-[#FFF] text-center
                  text-[14px] font-medium leading-[28px]
                  transition-colors
                  md:hover:border-[0.5px] md:hover:border-[#060]
                  md:hover:bg-[#060]
                  bg-[linear-gradient(200deg,_#090_0%,_#060_100%)]
                  shadow-[0_2px_16px_0_rgba(0,0,0,0.12)_inset,_0_0_32px_0_rgba(255,255,255,0.4)_inset,_0_0px_0px_2px_rgba(0,102,0,0.4)]
                "
                onClick={onClick}
              >
                <span>{t('landing.hero.cta')}</span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M11.3816 5.54742C11.702 5.22734 12.2089 5.20753 12.5525 5.48785L12.6189 5.54742L18.4519 11.3814C18.7936 11.7231 18.7936 12.277 18.4519 12.6187L12.6189 18.4517L12.5525 18.5123C12.2088 18.7925 11.7019 18.7721 11.3816 18.4517C11.0399 18.11 11.0399 17.5561 11.3816 17.2144L15.7214 12.8746H6.16675C5.6835 12.8746 5.29175 12.4828 5.29175 11.9996C5.29192 11.5165 5.68361 11.1246 6.16675 11.1246H15.7205L11.3816 6.7857L11.321 6.7193C11.0406 6.37562 11.0612 5.86782 11.3816 5.54742Z"
                    fill="white"
                  />
                </svg>
              </button>
            </div>

            {rotatingDescriptions.length > 0 && (
              <div className="max-w-5xl mx-auto md:mx-0 text-center md:text-center px-6 mb-6 order-4">
                <div className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-center md:text-center text-[16px] font-normal leading-[24px] max-w-3xl">
                  <p className="font-semibold md:mb-2 mb-0">
                    {rotatingDescriptions[currentDescriptionIndex]}
                  </p>
                  <p className="mb-0 md:text-[14px] text-[12px]">
                    {t('landing.hero.savings', {
                      amount: 100,
                      currencySymbol,
                    })}
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="relative hidden md:flex justify-center md:flex-1">
            <div className="relative">
              <div className="flex items-center justify-center relative">
                <div className="text-8xl px-8 md:px-0">
                  <img
                    src="/assets/hero-banner-image.png"
                    alt={t('landing.hero.imageAlt')}
                    className="mx-auto max-w-full max-h-[260px] md:max-h-[400px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Main component ──────────────────────────────────────────────────────
export default function PromoLandingPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('mealplan');

  const [promoMeal, setPromoMeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [livePromoProducts, setLivePromoProducts] = useState(null);
  const bottomHeroRef = useRef(null);
  const [hideStickyForBottomHero, setHideStickyForBottomHero] = useState(false);

  // Derived recipe from sourceRecipe
  const recipe = promoMeal?.sourceRecipe || null;

  // Prefer matched products embedded at save time, fall back to products fetched
  // live from Typesense (see effect below) so legacy promo meals still render
  // localized product names + images.
  const promoProductList = useMemo(() => {
    const saved = recipe?._matchedProducts;
    if (Array.isArray(saved) && saved.length) return [{ items: saved }];
    return livePromoProducts;
  }, [recipe, livePromoProducts]);

  // Live Typesense match for legacy promo meals (no embedded _matchedProducts).
  // Runs once per recipe load; uses the same Phases 1-3 used by the meal-plan
  // flow so product shape, lang map, sizes, and images are identical.
  useEffect(() => {
    if (!recipe?.ingredients?.length) return;
    if (Array.isArray(recipe._matchedProducts) && recipe._matchedProducts.length) return;

    let cancelled = false;
    const country = getUserCountrySync();
    matchIngredientsForBatch([recipe], country, 1)
      .then((grouped) => {
        if (cancelled) return;
        if (Array.isArray(grouped) && grouped.length) {
          setLivePromoProducts(grouped);
        }
      })
      .catch((err) => {
        console.warn('[PromoLandingPage] live Typesense match failed:', err);
      });

    return () => { cancelled = true; };
  }, [recipe]);

  // ── Fetch promo meal ─────────────────────────────────────────────────
  useEffect(() => {
    if (!id) {
      navigate('/', { replace: true });
      return;
    }
    let cancelled = false;

    backendClient.get(`/api/promo/${id}`, { timeout: 10000 })
      .then(data => {
        if (cancelled) return;
        if (data?.promoMeal) {
          setPromoMeal(data.promoMeal);
        } else {
          setError(true);
          navigate('/', { replace: true });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
          navigate('/', { replace: true });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [id, navigate]);

  // ── Analytics: page view ────────────────────────────────────────────
  useEffect(() => {
    if (!promoMeal) return;
    // Extract UTM params from URL
    const params = new URLSearchParams(window.location.search);
    trackPromoLandingViewed({
      promo_meal_id: id,
      promo_meal_title: promoMeal.title,
      utm_source: params.get('utm_source'),
      utm_campaign: params.get('utm_campaign'),
    });
  }, [promoMeal, id]);

  useEffect(() => {
    const heroEl = bottomHeroRef.current;
    if (!heroEl || !('IntersectionObserver' in window)) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        setHideStickyForBottomHero(entries[0]?.isIntersecting || false);
      },
      { root: null, threshold: 0 }
    );

    observer.observe(heroEl);
    return () => observer.disconnect();
  }, [promoMeal]);

  // ── CTA click ────────────────────────────────────────────────────────
  const handleCTA = useCallback(() => {
    if (promoMeal) {
      trackPromoCTAClicked({
        promo_meal_id: id,
        promo_meal_title: promoMeal.title,
        cta_text: t('promoLanding.ctaButton'),
      });
    }
    navigate('/wizard');
  }, [promoMeal, id, navigate, t]);

  // ─── Loading ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F8F7] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-[#FFCB00] border-t-transparent rounded-full animate-spin" />
          <p className="text-[#65635D] text-[14px]">{t('promoLanding.loadingMeal')}</p>
        </div>
      </div>
    );
  }

  if (error || !promoMeal) {
    return (
      <div className="min-h-screen bg-[#F8F8F7] flex items-center justify-center px-6">
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-[#353227] text-[16px]">{t('promoLanding.notFound')}</p>
          <button
            onClick={() => navigate('/')}
            className="text-[#FFCB00] font-semibold underline text-[14px]"
          >
            {t('promoLanding.backHome')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-[#F8F8F7] promo-landing-page-root"
      
    >
      <div className="max-w-5xl mx-auto px-5 pt-6 md:pt-8">
        <div className="w-full mb-6">
          {promoMeal.imageURL ? (
            <img
              src={promoMeal.imageURL}
              alt={promoMeal.title}
              className="rounded-[12px] w-full object-cover max-h-96 md:aspect-[16/5] aspect-[16/10]"
            />
          ) : (
            <div className="rounded-[12px] w-full h-64 bg-gray-100 flex items-center justify-center">
              <span className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[14px]">
                {t('loading.imageNotAvailable')}
              </span>
            </div>
          )}
        </div>

        <RecipeContent
          recipe={recipe}
          promoMeal={promoMeal}
          isInline={true}
          title={promoMeal.title}
          showSectionTabs={true}
          productList={promoProductList}
        />
      </div>

      <div ref={bottomHeroRef}>
        <PromoBottomHero onClick={handleCTA} />
      </div>

      <PromoStickyCTA
        onClick={handleCTA}
        label={t('promoLanding.ctaButton')}
        hidden={hideStickyForBottomHero}
      />
    </div>
  );
}
